import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { EmbeddedPage } from '@tinct/core-api-models';
import { getCampaignById, useGenerateAdPage, useGenerateAll } from '@tinct/core-api';
import {
  useCurrentWorkspaceUrlPrefix,
  useGetCurrentQuotas,
  useGetCurrentWorkspaceId,
} from '@tinct/core-workspace';
import { useOpenPaywall } from '@tinct/web-feature-payment';
import { pageNeedsGeneration } from '../pages/shared';

/**
 * Smallest projection the quota gate needs. Kept separate from the callers'
 * campaign queries so the funnel never carries a page list it only needs at
 * the moment the user clicks.
 */
const QUOTA_PAGE_FIELDS = {
  id: null,
  pages: {
    limit: 1000,
    fields: {
      id: null,
      status: null,
      outdatedStatus: null,
      // Ad-campaign variants generate through their own endpoint — the routing
      // below needs to know which pages those are.
      adCampaign: { fields: { id: null } },
    },
  },
} as const;

type GenerateOptions = {
  /**
   * Runs once the quota gate has passed and before generation starts. Use it
   * to dismiss the dialog the action was triggered from: when the paywall
   * opens instead, the dialog is deliberately left in place.
   */
  onStart?: () => void;
};

/**
 * The campaign-wide "Generate all" action, shared by the preview step and by
 * every funnel step offering "Generate & review" after an edit outdated a live
 * campaign.
 *
 * The quota gate lives here rather than at each call site so the rule for
 * which pages consume a credit (`pageNeedsGeneration`) has exactly one
 * definition — the copies previously drifted from what the backend actually
 * generates, which let the paywall be skipped entirely.
 *
 * Pass `pages` only when the caller already holds the list (the preview step
 * does). Otherwise the gate fetches it on click, which keeps a 1000-page
 * payload off every funnel mount and counts against the campaign as it stands
 * after the edit that triggered the action, not as it was when the step
 * mounted. A supplied list must project `status` and `outdatedStatus` (or
 * stale pages go uncounted) plus `adCampaign.id`, or ad-campaign variants
 * fall through to the campaign-wide endpoint that ignores them.
 */
export function useGenerateAllPages(campaignId: string, pages?: EmbeddedPage[] | null) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspaceId = useGetCurrentWorkspaceId();
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const { lpGenerationQuota } = useGetCurrentQuotas();
  const { openPaywall } = useOpenPaywall();
  const { mutateAsync: triggerGenerateAll } = useGenerateAll();
  const { mutateAsync: generateAdPage } = useGenerateAdPage();

  const resolvePages = useCallback(async (): Promise<EmbeddedPage[]> => {
    if (pages) return pages;

    // `staleTime: 0` so a repeat click re-counts: the default is Infinity, and
    // a cached list from before the last edit would gate on the wrong number.
    const campaign = await queryClient.fetchQuery({
      queryKey: ['campaign', campaignId, QUOTA_PAGE_FIELDS, undefined],
      queryFn: () => getCampaignById({ id: campaignId, fields: QUOTA_PAGE_FIELDS }),
      staleTime: 0,
    });
    return campaign.pages ?? [];
  }, [pages, campaignId, queryClient]);

  /**
   * Fires generation for every page that needs it. Returns false — having
   * opened the paywall — when the workspace is short on credits.
   */
  const generateAll = useCallback(
    async ({ onStart }: GenerateOptions = {}) => {
      const pagesNeedingGeneration = (await resolvePages()).filter(
        pageNeedsGeneration,
      );
      if (
        pagesNeedingGeneration.length > 0 &&
        (lpGenerationQuota?.remainingQuota ?? 0) < pagesNeedingGeneration.length
      ) {
        openPaywall('not_enough_credits');
        return false;
      }

      // The campaign-wide endpoint generates from leads and silently skips
      // lead-less pages, so ad-campaign variants (ADWORD axis) must go through
      // their own endpoint — idempotent on the page row, so it doubles as the
      // retry for a stuck variant.
      const adsCampaignIds = pagesNeedingGeneration
        .map((page) => page.adCampaign?.id)
        .filter((id): id is string => !!id);
      const leadPagesCount = pagesNeedingGeneration.length - adsCampaignIds.length;

      onStart?.();
      try {
        // Keep the campaign-wide call when lead pages need work — and when
        // nothing does, preserving its "nothing to generate" behavior — but
        // skip it when every needing page is an ad variant it would ignore.
        if (leadPagesCount > 0 || adsCampaignIds.length === 0) {
          await triggerGenerateAll({ id: campaignId, organizationId: workspaceId });
        }
        await Promise.all(
          adsCampaignIds.map((adsCampaignId) =>
            generateAdPage({ campaignId, adsCampaignId }),
          ),
        );
      } catch (e) {
        console.error('Error triggering generate-all:', e);
      }
      return true;
    },
    [
      resolvePages,
      lpGenerationQuota?.remainingQuota,
      openPaywall,
      triggerGenerateAll,
      generateAdPage,
      campaignId,
      workspaceId,
    ],
  );

  /**
   * Same, then lands on the preview step's variants tab so it opens on the
   * pages being regenerated, with jobs already queued/running and the
   * generating overlay showing.
   */
  const generateAllAndReview = useCallback(
    async (options?: GenerateOptions) => {
      if (!(await generateAll(options))) return false;
      navigate(`${workspaceUrlPrefix}/funnel-campaigns/${campaignId}/preview?view=variants`);
      return true;
    },
    [generateAll, navigate, workspaceUrlPrefix, campaignId],
  );

  return { generateAll, generateAllAndReview };
}
